/**
 * 富文本编辑器配置工具
 * 提供统一的工具栏配置和编辑器配置
 */

// 默认工具栏配置
export const defaultToolbarConfig = {
    toolbarKeys: [
        'headerSelect',
        'blockquote',
        '|',
        'bold',
        'underline',
        'italic',
        {
            key: 'group-more-style',
            title: '更多样式',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M204.8 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M505.6 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M806.4 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path></svg>',
            menuKeys: ['through', 'code', 'sup', 'sub', 'clearStyle']
        },
        'color',
        'bgColor',
        '|',
        'fontSize',
        'fontFamily',
        'lineHeight',
        '|',
        'bulletedList',
        'numberedList',
        'todo',
        {
            key: 'group-justify',
            title: '对齐',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z"></path></svg>',
            menuKeys: ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyJustify']
        },
        {
            key: 'group-indent',
            title: '缩进',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M0 64h1024v128H0z m384 192h640v128H384z m0 192h640v128H384z m0 192h640v128H384zM0 832h1024v128H0z m0-128V320l256 192z"></path></svg>',
            menuKeys: ['indent', 'delIndent']
        },
        // '|',
        // 'emotion',
        // 'insertLink',
        // {
        //     key: 'group-image',
        //     title: '图片',
        //     iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M959.877 128l0.123 0.123v767.775l-0.123 0.122H64.102l-0.122-0.122V128.123l0.122-0.123h895.775zM960 64H64C28.795 64 0 92.795 0 128v768c0 35.205 28.795 64 64 64h896c35.205 0 64-28.795 64-64V128c0-35.205-28.795-64-64-64zM832 288.01c0 53.023-42.988 96.01-96.01 96.01s-96.01-42.987-96.01-96.01S682.967 192 735.99 192 832 234.988 832 288.01zM896 832H128V704l224.01-168 96.01 72 128.01-96 319.97 192z"></path></svg>',
        //     menuKeys: ['insertImage', 'uploadImage']
        // },
        'insertTable',
        // 'codeBlock',
        'divider',
        '|',
        'undo',
        'redo',
        '|',
        'fullScreen'
    ]
}

// 简化版工具栏配置（适用于简单场景）
export const simpleToolbarConfig = {
    toolbarKeys: [
        'bold',
        'underline',
        'italic',
        '|',
        'color',
        'bgColor',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'insertLink',
        'insertImage',
        '|',
        'undo',
        'redo'
    ]
}

// 基础版工具栏配置（适用于基础场景）
export const basicToolbarConfig = {
    toolbarKeys: [
        'bold',
        'underline',
        'italic',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'undo',
        'redo'
    ]
}

/**
 * 创建编辑器配置
 * @param {Object} options 配置选项
 * @param {string} options.placeholder 占位符文本
 * @param {string} options.uploadImageUrl 图片上传地址
 * @param {string} options.uploadVideoUrl 视频上传地址
 * @param {number} options.maxFileSize 最大文件大小（MB）
 * @param {Array} options.allowedImageTypes 允许的图片类型
 * @param {Array} options.allowedVideoTypes 允许的视频类型
 * @param {Object} options.headers 请求头
 * @param {Object} options.meta 额外参数
 * @returns {Object} 编辑器配置
 */
export function createEditorConfig(options = {}) {
    const {
        placeholder = '请输入内容...',
        uploadImageUrl = '/api/upload-image',
        uploadVideoUrl = '/api/upload-video',
        maxFileSize = 5,
        allowedImageTypes = ['image/*'],
        allowedVideoTypes = ['video/*'],
        headers = {},
        meta = {}
    } = options

    return {
        placeholder,
        MENU_CONF: {
            // 配置上传图片
            uploadImage: {
                server: uploadImageUrl,
                fieldName: 'file',
                maxFileSize: maxFileSize * 1024 * 1024,
                allowedFileTypes: allowedImageTypes,
                headers,
                meta,
                metaWithUrl: false,
                withCredentials: false,
                timeout: 15 * 1000,
                onBeforeUpload(file) {
                    console.log('onBeforeUpload', file)
                    return file
                },
                onProgress(progress) {
                    console.log('onProgress', progress)
                },
                onSuccess(file, res) {
                    console.log('onSuccess', file, res)
                },
                onFailed(file, res) {
                    console.log('onFailed', file, res)
                },
                onError(file, err, res) {
                    console.log('onError', file, err, res)
                }
            },
            // 配置上传视频
            uploadVideo: {
                server: uploadVideoUrl,
                fieldName: 'file',
                maxFileSize: maxFileSize * 10 * 1024 * 1024, // 视频文件通常更大
                allowedFileTypes: allowedVideoTypes,
                headers,
                meta,
                metaWithUrl: false,
                withCredentials: false,
                timeout: 60 * 1000,
                onBeforeUpload(file) {
                    console.log('onBeforeUpload', file)
                    return file
                },
                onProgress(progress) {
                    console.log('onProgress', progress)
                },
                onSuccess(file, res) {
                    console.log('onSuccess', file, res)
                },
                onFailed(file, res) {
                    console.log('onFailed', file, res)
                },
                onError(file, err, res) {
                    console.log('onError', file, err, res)
                }
            }
        }
    }
}

/**
 * 创建工具栏配置
 * @param {string} type 工具栏类型：'default' | 'simple' | 'basic'
 * @param {Array} customKeys 自定义工具栏键
 * @returns {Object} 工具栏配置
 */
export function createToolbarConfig(type = 'default', customKeys = null) {
    let config
    
    switch (type) {
        case 'simple':
            config = simpleToolbarConfig
            break
        case 'basic':
            config = basicToolbarConfig
            break
        default:
            config = defaultToolbarConfig
    }
    
    if (customKeys) {
        config = {
            ...config,
            toolbarKeys: customKeys
        }
    }
    
    return config
}

/**
 * 获取默认的编辑器配置
 * @param {string} placeholder 占位符
 * @returns {Object} 默认编辑器配置
 */
export function getDefaultEditorConfig(placeholder = '请输入内容...') {
    return createEditorConfig({ placeholder })
}

/**
 * 获取默认的工具栏配置
 * @returns {Object} 默认工具栏配置
 */
export function getDefaultToolbarConfig() {
    return defaultToolbarConfig
}
